import React from 'react';
import cn from 'classnames';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import styles from './SelectAccountTypePanel.module.scss';
import SVGWrapper from '@repo/icons/SVGWrapper';
import { NavPeopleIcon } from '@repo/icons';

export type AccountType = 'pro' | 'teams';

const TeamPlanIcon = () => (
  <SVGWrapper width="44" height="44" viewBox="0 0 44 44" strokeWidth="2" customColor="#fff">
    <path
      fill="none"
      d="M22 24.1c4.13 0 8.25 6.08 8.25 6.84 0 .76-4.13 1.23-8.25 1.23-4.13 0-8.25-.47-8.25-1.23S17.88 24.1 22 24.1ZM26.13 16.03A4.08 4.08 0 0 1 22 20.07a4.08 4.08 0 0 1-4.13-4.04A4.08 4.08 0 0 1 22 12c2.28 0 4.13 1.8 4.13 4.03Z"
    />
    <path
      fill="none"
      d="M11.69 14.02c-1.22.35-2.07 1.6-2.07 3.02 0 1.43.85 2.67 2.07 3.03M32.31 14.02c1.22.35 2.06 1.6 2.06 3.02 0 1.43-.84 2.67-2.06 3.03"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fill="none"
      d="M9.63 24.1C7.4 25.28 5.5 28.7 5.5 29.2s1 .65 4.11.95M34.38 24.1c2.22 1.18 4.12 4.6 4.12 5.1s-1 .65-4.11.95"
      strokeLinecap="round"
    />
  </SVGWrapper>
);

export const CheckmarkIcon = ({ className }) => (
  <SVGWrapper
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    strokeWidth="1.17"
    customColor="#fff"
  >
    <path
      fill="none"
      d="M4.5 8.6l2.2 2.3l4.8 -5.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="10.8 10.8"
    />
  </SVGWrapper>
);

export default function SelectAccountTypePanel({
  proceedToNextStep,
  accountTypePreference,
  setAccountTypePreference,
  loadingProductDetails,
}: {
  proceedToNextStep: () => void;
  accountTypePreference?: AccountType;
  setAccountTypePreference: (accountType: AccountType) => void;
  loadingProductDetails: boolean;
}) {
  return (
    <Panel>
      <CopySection>
        <h2>How are you planning to use Clay?</h2>
        <p>We will streamline your setup experience accordingly.</p>
        <FinishStepButton
          onClick={proceedToNextStep}
          disabled={!accountTypePreference}
          disabledMsg="Make a selection to continue"
          loading={loadingProductDetails}
        >
          Continue
        </FinishStepButton>
      </CopySection>
      <div className={styles.accountTypeOptions}>
        <div
          className={cn(styles.option, accountTypePreference === 'teams' && styles.selected)}
          onClick={() => setAccountTypePreference('teams')}
        >
          <TeamPlanIcon />
          <div className={styles.title}>With others</div>
          <div className={styles.description}>Collaborate with your whole team.</div>
          <div className={styles.checkbox}>
            <CheckmarkIcon className={styles.checkmark} />
          </div>
          <div className={styles.border} />
        </div>
        <div
          className={cn(styles.option, accountTypePreference === 'pro' && styles.selected)}
          onClick={() => setAccountTypePreference('pro')}
        >
          <NavPeopleIcon customColor="#fff" width="34" height="34" strokeWidth="1.35" />
          <div className={styles.title}>For myself</div>
          <div className={styles.description}>All your relationships in one place.</div>
          <div className={styles.checkbox}>
            <CheckmarkIcon className={styles.checkmark} />
          </div>
          <div className={styles.border} />
        </div>
      </div>
    </Panel>
  );
}
